<script setup>
import {
  ref, watch, nextTick, useTemplateRef,
} from 'vue';
import ComponentOpcionHorarios from '@/components/inputs/ComponentOpcionHorarios.vue';
import InputFechaHora from '@/components/cifra/InputFechaHora.vue';
import SelectCifraAjax from '@/components/inputs/SelectCifraAjax.vue';
import SelectorEstructura from '@/components/SelectorEstructura.vue';
import { onlyISODate } from '../../../libs/dates';
import asistenciasApi from '../../../api/cuadernos/asistencias';
import ensenanzasApi from '../../../api/cifra/ensenanzas';

import CuadernoAsistencia from '../cuadernos/CuadernoAsistencia.vue';

const URL_ASIGNATURAS_SELECT = '/asignaturas/json?format=select2&inferiores=true&incluir_hijas=true&mostrar_horario=true';

defineProps({
  mostrarFechaEnCuaderno: {
    type: Boolean,
    default: true,
  },
});

const grupoId = ref(null);
const fecha = ref(null);
const asignatura = ref(null);
const cuaderno = ref(null);
const tipoAusencia = ref(null); // null = no determinado, 'sesiones' | 'dias'
const horarioSeleccionado = ref(null);

const selectAsignaturaRef = useTemplateRef('selectAsignaturaRef');

// Control de race conditions en cargarCuaderno
let cuadernoRequestToken = 0;
let cuadernoAbortController = null;

const paramsAsignatura = (texto) => ({
  q: texto,
  grupo_id: grupoId.value,
  fecha: fecha.value,
});

const cargarCuaderno = async () => {
  cuaderno.value = null;
  horarioSeleccionado.value = null;

  // Cancelar petición anterior si existe
  if (cuadernoAbortController) {
    cuadernoAbortController.abort();
  }

  // Incrementar token para invalidar respuestas antiguas
  cuadernoRequestToken += 1;
  const tokenActual = cuadernoRequestToken;

  // Crear nuevo AbortController para esta petición
  cuadernoAbortController = new AbortController();

  try {
    if (tipoAusencia.value === 'sesiones') {
      if (!grupoId.value || !asignatura.value) {
        return;
      }
      const asignaturaId = asignatura.value.asignatura_id;
      const horarioId = asignatura.value.horario_id || asignatura.value.id;
      const params = { grupo_id: grupoId.value, asignatura_id: asignaturaId, horario_id: horarioId };
      const { data } = await asistenciasApi.infoCuadernoSession(params, {
        signal: cuadernoAbortController.signal,
      });

      // Solo asignar si este es el token más reciente
      if (tokenActual === cuadernoRequestToken) {
        cuaderno.value = data;
      }
    } else if (tipoAusencia.value === 'dias') {
      if (!grupoId.value || !fecha.value) {
        return;
      }
      const params = {
        grupo_id: grupoId.value,
        fecha: fecha.value,
      };
      const { data } = await asistenciasApi.infoCuadernoMasTemprano(params, {
        signal: cuadernoAbortController.signal,
      });

      // Solo asignar si este es el token más reciente
      if (tokenActual === cuadernoRequestToken) {
        cuaderno.value = data;
      }
    }
  } catch (error) {
    // No registrar error si fue cancelado (AbortError)
    if (error.name !== 'AbortError') {
      console.error('Error al cargar cuaderno:', error);
      cuaderno.value = null;
    }
  }
};

const determinarTipoAusenciaDesdePath = async (path) => {
  if (!path || !path.length) {
    tipoAusencia.value = null;
    return;
  }

  const ensennanzaNode = path.find((p) => p.type === 'ensennanza');
  if (!ensennanzaNode || !ensennanzaNode.id) {
    tipoAusencia.value = null;
    return;
  }

  try {
    const { data } = await ensenanzasApi.obtenerConfiguracion(ensennanzaNode.id);
    const configuracion = data?.configuracion || {};
    const tipo = configuracion.tipo_ausencia || 'sesiones';
    tipoAusencia.value = (tipo === 'dias' ? 'dias' : 'sesiones');
  } catch (error) {
    console.error('Error al determinar tipo de ausencia:', error);
    tipoAusencia.value = 'sesiones';
  }
};

const handleSelectorEstructuraSeleccionada = ({ _tipo, id, path }) => {
  grupoId.value = id;
  asignatura.value = null;
  determinarTipoAusenciaDesdePath(path);
};

const recargarAsignaturas = () => {
  nextTick(() => {
    const selectRef = selectAsignaturaRef.value;
    if (selectRef?.load) {
      selectRef.load();
    }
  });
};

// Watches
watch(() => asignatura.value, () => {
  horarioSeleccionado.value = null;
  cargarCuaderno();
});

watch(() => tipoAusencia.value, (newVal) => {
  if (newVal === 'dias' && grupoId.value && fecha.value) {
    cargarCuaderno();
  } else if (newVal === 'sesiones') {
    recargarAsignaturas();
  }
});

watch(() => grupoId.value, (newVal) => {
  asignatura.value = null;
  horarioSeleccionado.value = null;

  if (!newVal) {
    fecha.value = null;
    return;
  }

  if (grupoId.value && !fecha.value) {
    setTimeout(() => {
      fecha.value = onlyISODate(new Date());
    }, 100);
  } else if (grupoId.value && fecha.value && tipoAusencia.value === 'sesiones') {
    recargarAsignaturas();
  }
});

watch(() => fecha.value, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  asignatura.value = null;
  horarioSeleccionado.value = null;

  if (fecha.value && grupoId.value && tipoAusencia.value === 'sesiones') {
    recargarAsignaturas();
  } else if (fecha.value && grupoId.value && tipoAusencia.value === 'dias') {
    cargarCuaderno();
  }
});


</script>

<template>
  <div class="asistencia-pasar-lista">
    <div class="card">
      <div class="card-body can-overflow">
        <div class="d-flex gap-3 flex-wrap w-100">
          <!-- Selector de estructura -->
          <div class="flex-grow-1">
            <SelectorEstructura
              ref="selectorEstructuraRef"
              :tipos-seleccionables="['grupo']"
              :solo-anno-actual="true"
              @seleccionada="handleSelectorEstructuraSeleccionada"
            />
          </div>

          <!-- Selector de fecha -->
          <div class="flex-shrink-0">
            <InputFechaHora
              v-model="fecha"
              :disabled="!grupoId"
              position="bottom right"
            />
          </div>

          <!-- Selector de asignatura (solo modo sesiones) -->
          <div v-if="tipoAusencia === 'sesiones'" class="flex-grow-1">
            <SelectCifraAjax
              ref="selectAsignaturaRef"
              :url="URL_ASIGNATURAS_SELECT"
              :placeholder="$t('Asignatura')"
              :searchable="false"
              v-model="asignatura"
              :params="paramsAsignatura"
              :disabled="!grupoId || !fecha"
            >
              <template #option="{ option }">
                <ComponentOpcionHorarios :text="option.text" />
              </template>
            </SelectCifraAjax>
          </div>
        </div>
      </div>
    </div>

    <!-- Cuaderno de asistencia -->
    <div class="h-100 mt-3">
      <CuadernoAsistencia
        v-if="cuaderno"
        :cuaderno-id="cuaderno.id"
        :fecha="fecha"
        :fechas-validas="cuaderno.fechas_validas"
        :horario-id="cuaderno.horario.id"
        :tipos-asistencia="cuaderno.tipos_asistencia"
        :admin="true"
        :mostrar-fecha="mostrarFechaEnCuaderno"
      />
      <div v-else-if="!cuaderno" class="alert alert-info mt-3">
        {{ $t('seleccione_cuaderno') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.asistencia-pasar-lista {
  height: 100%;
  display: flex;
  flex-direction: column;

  .card {
    flex-shrink: 0;
  }

  > div:last-child {
    flex: 1;
    overflow: visible;
  }

  :deep(.max-height) {
    overflow: visible !important;
  }
}
</style>
